import React, { useEffect, useMemo, useRef, useState } from 'react';
import useDropdownNav from './useDropdownNav';
import './SearchSelect.css';

// ONE THING (or several — `multiple`), CHOSEN BY TYPING ITS NAME.
//
// A native <select> is fine for five options and useless for two hundred: a chain's hotels, the
// platform's backend systems, a partner's credentials. You cannot search it, you cannot see what you
// are scrolling past, and on a long list the only way through is the mouse.
//
// This is the control the top bar uses — search box, filtered list, keyboard navigation through
// useDropdownNav — as a form field, so a screen that needs one of a long list asks for it the way the
// rest of the site does.
//
// GENERIC BY EXTRACTION, NOT BY GUESS. It began as HotelSelect and was pulled out the second thing
// needed it (the Universal API integrations dialog), so what is configurable here is what a second
// caller actually varied: the items, the labels, and what "nothing matches" should say. HotelSelect is
// now a thin adapter over it rather than a second implementation — the search rule, the keyboard
// handling and the empty state are the parts that get done differently every time they are done twice.

/**
 * Matches on the label or the code, case-insensitively, on any substring — somebody who knows a thing
 * by its code types that, and somebody who knows it by name types part of the name.
 */
const matches = (item, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return String(item.label || '').toLowerCase().includes(q)
        || String(item.code || '').toLowerCase().includes(q);
};

/**
 * @param {Array}    items          [{ id, label, code? }] — `code` is the muted monospace suffix
 * @param {*}        value          the chosen id, or null
 * @param {Function} onChange       (id) => void
 * @param {boolean} [disabled]
 * @param {string}  [placeholder]   what the closed control says when nothing is chosen
 * @param {string}  [searchPlaceholder]
 * @param {boolean} [allowClear]    offer "none" as a choice
 * @param {string}  [clearLabel]    what that choice is called
 * @param {string}  [valueLabel]    what to show for a chosen id that is NOT in `items` — a record can
 *                                  name something outside the current scope, and showing the
 *                                  placeholder there reads as "nothing chosen", which is a different
 *                                  and wrong statement
 * @param {string}  [valueLabelNote] the muted note beside such a value
 * @param {string}  [emptyLabel]    shown when `items` is empty (a different thing from "no match")
 * @param {boolean} [multiple]      SEVERAL THINGS: `value` is an array of ids and onChange gets the new array. A
 *                                  click ticks or unticks and the list stays open; "none" empties it. The closed
 *                                  control names the one chosen, or how many ("2 chosen" — `countLabel(n)`).
 * @param {Function} [countLabel]   (n) => what the closed control says when several are chosen
 */
const SearchSelect = ({
    items = [],
    value = null,
    onChange,
    disabled = false,
    placeholder = 'Choose…',
    searchPlaceholder = 'Search…',
    allowClear = false,
    clearLabel = 'None',
    valueLabel = null,
    valueLabelNote = 'not in this list',
    emptyLabel = 'Nothing to choose from.',
    id,
    multiple = false,
    countLabel = (n) => `${n} chosen`,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const box = useRef(null);

    const picked = useMemo(
        () => new Set(multiple ? (value || []).map(String) : value != null ? [String(value)] : []),
        [multiple, value]);
    const isPicked = (itemId) => picked.has(String(itemId));
    const chosen = useMemo(
        () => (multiple && picked.size !== 1 ? null : (items || []).find(i => isPicked(i.id)) || null),
        [items, picked]); // eslint-disable-line
    const several = multiple && picked.size > 1;

    const filtered = useMemo(() => (items || []).filter(i => matches(i, query)), [items, query]);

    const close = () => { setOpen(false); setQuery(''); };

    // `choose` is stable enough for the memo below: it only closes over onChange, and a caller that
    // re-creates onChange every render simply rebuilds the list, which is cheap.
    const choose = (next) => {
        if (!multiple) { onChange(next); close(); return; }
        // Several: "none" empties the set; an item toggles in or out, and the list stays open for the next.
        if (next === null) { onChange([]); close(); return; }
        const list = (value || []).filter(v => String(v) !== String(next));
        onChange(list.length === (value || []).length ? [...list, next] : list);
    };
    const navItems = useMemo(() => ([
        ...(allowClear && !query ? [{ key: 'none', select: () => choose(null) }] : []),
        ...filtered.map(i => ({ key: i.id, select: () => choose(i.id) })),
    ]), [filtered, allowClear, query, onChange, value]); // eslint-disable-line

    const nav = useDropdownNav({ isOpen: open, items: navItems, onClose: close, query });

    // Clicking anywhere else closes it — a dropdown left open over the form is the thing that swallows
    // the next click somebody makes on the field beneath it.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (box.current && !box.current.contains(e.target)) close(); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const offset = allowClear && !query ? 1 : 0;

    return (
        <div className={`hsel${disabled ? ' is-disabled' : ''}`} ref={box}>
            <button
                type="button"
                id={id}
                className="hsel-control"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => (open ? close() : setOpen(true))}
            >
                <span className={`hsel-value${chosen || several || (!multiple && value && valueLabel) ? '' : ' is-empty'}`}>
                    {several ? countLabel(picked.size) : chosen ? chosen.label : (!multiple && value && valueLabel) || placeholder}
                    {chosen && chosen.code && <span className="hsel-code">{chosen.code}</span>}
                    {!multiple && !chosen && value && valueLabel && <span className="hsel-code">{valueLabelNote}</span>}
                </span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            </button>

            {open && (
                <div className="hsel-menu" role="listbox">
                    <div className="hsel-search">
                        <input
                            type="text"
                            className="hsel-search-input"
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={nav.onKeyDown}
                            autoFocus
                        />
                    </div>
                    <div className="hsel-list">
                        {allowClear && !query && (
                            <button
                                type="button"
                                {...nav.navProps(0)}
                                className={`hsel-item${!picked.size ? ' is-chosen' : ''}${nav.isActive(0) ? ' kb-active' : ''}`}
                                onClick={() => choose(null)}
                            >
                                <span className="hsel-item-name">{clearLabel}</span>
                            </button>
                        )}
                        {filtered.map((item, i) => (
                            <button
                                type="button"
                                key={item.id}
                                {...nav.navProps(i + offset)}
                                className={`hsel-item${isPicked(item.id) ? ' is-chosen' : ''}`
                                    + `${nav.isActive(i + offset) ? ' kb-active' : ''}`}
                                onClick={() => choose(item.id)}
                                {...(multiple ? { role: 'option', 'aria-selected': isPicked(item.id) } : {})}
                            >
                                {multiple && <input type="checkbox" readOnly tabIndex={-1} checked={isPicked(item.id)}
                                    style={{ marginRight: '0.5rem', pointerEvents: 'none' }} />}
                                <span className="hsel-item-name">{item.label}</span>
                                {item.code && <span className="hsel-item-code">{item.code}</span>}
                            </button>
                        ))}
                        {!filtered.length && (
                            <p className="hsel-none">
                                {items.length ? `Nothing matches “${query}”.` : emptyLabel}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchSelect;
